import { useContext, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DateTime } from "luxon";
import { PageContainer } from "@packsize/components";
import { Card, Box, Stack, Typography, useTheme, Divider } from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { useUser } from "@packsize/authentication";

import { ProductionContext } from "@/contexts/production";
import { getJobContentsString } from "@/views/production/orders/utils/utils";
import { isScanToTriggerConfigured } from "@/views/configuration/production-groups/manual-scan-to-produce-dropdown";
import { useBatchHistory } from "@/views/production/orders/query/batch-history";
import { OrdersHistoryActionMenu } from "@/views/production/orders/history/orders-history-action-menu";
import { BackButton } from "@/components/details-container";
import { type BatchHistoryItem } from "@/api/audit-api";
import { FailedRetry } from "@/components/failed-retry";

const OrdersHistory = () => {
	const intl = useIntl();
	const theme = useTheme();
	const { currentUser } = useUser();
	const { currentMachineGroup, currentProductionGroup } = useContext(ProductionContext);
	const scanToTriggerConfigured = isScanToTriggerConfigured(currentProductionGroup?.manualScanToProduce);

	const { data, isLoading, error } = useBatchHistory({
		machineGroupId: currentMachineGroup?.id,
		currentUserId: currentUser?.id,
	});

	const columns: GridColDef<BatchHistoryItem>[] = useMemo(
		() => [
			{
				field: "statusLastUpdatedUTC",
				headerName: intl.formatMessage({ id: "Date", defaultMessage: "Date" }),
				flex: 1.5,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (value) => DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT),
			},
			{
				field: "alias",
				headerName: intl.formatMessage({ id: "Name", defaultMessage: "Name" }),
				flex: 2,
				sortable: false,
				disableColumnMenu: true,
			},
			{
				field: "licensePlateNumber",
				headerName: intl.formatMessage({ id: "Trigger Value", defaultMessage: "Trigger Value" }),
				flex: 2,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (value) => value || "",
			},
			{
				field: "contents",
				headerName: intl.formatMessage({ id: "Contents", defaultMessage: "Contents" }),
				flex: 1,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) =>
					getJobContentsString({
						cartonCountPerJob: row.cartonCountPerJob,
						labelCountPerJob: row.labelCountPerJob,
						cartonDimension: row.cartonDimension,
						intl,
					}),
			},
			{
				field: "quantity",
				headerName: intl.formatMessage({ id: "Qty", defaultMessage: "Qty" }),
				flex: 0.25,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) => `${row.completedCount}/${row.quantity}`,
			},
			{
				field: "status",
				headerName: intl.formatMessage({ id: "Status", defaultMessage: "Status" }),
				flex: 0.75,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (value) => intl.formatMessage({ id: value, defaultMessage: "Unknown status" }),
			},
			{
				field: "actions",
				headerName: "",
				width: 62,
				sortable: false,
				disableColumnMenu: true,
				align: "center",
				renderCell: ({ row }) => <OrdersHistoryActionMenu batchId={row.batchId} />,
			},
		],
		[intl],
	);

	const columnVisibilityModel = useMemo(
		() => ({ licensePlateNumber: scanToTriggerConfigured }),
		[scanToTriggerConfigured],
	);

	if (error) {
		return <FailedRetry retry={() => window.location.reload()} />;
	}

	return (
		<PageContainer sx={{ gap: 2 }}>
			<BackButton />

			<Card sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: 2, gap: 2 }}>
					<Typography variant="h5">
						<FormattedMessage id="Recently Produced" defaultMessage="Recently Produced" />
					</Typography>
				</Stack>

				<Divider sx={{ margin: 0 }} />
				<Box sx={{ width: "100%", flex: 1, minHeight: 0, overflowY: "scroll" }}>
					<DataGridPro
						autoHeight
						rows={data ?? []}
						columns={columns}
						columnVisibilityModel={columnVisibilityModel}
						getRowId={(row) => row.batchId}
						loading={isLoading}
						hideFooter
						disableRowSelectionOnClick
						disableColumnResize
						disableColumnReorder
						sx={{
							border: "none",
							"& .MuiDataGrid-row:nth-of-type(odd)": {
								backgroundColor: theme.palette.grey[100],
							},
							"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
								outline: "none",
							},
							"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
								outline: "none",
							},
						}}
					/>
				</Box>
			</Card>
		</PageContainer>
	);
};

export default OrdersHistory;
